/**
 * MandatoryPhotoGate — blocking modal that forces a user without a profile photo
 * to upload or capture one before they can continue using the app.
 *
 * Shown as a child of Layout. The user chose "obligatoria desde el primer login"
 * so no "later" button is rendered.
 */
import React, { useEffect, useState } from "react";
import PhotoCapture from "@/components/PhotoCapture";
import api from "@/lib/api";
import { toast } from "sonner";
import { CameraRotate, SealCheck, HandWaving } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function MandatoryPhotoGate() {
  const { user, checkAuth } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [justUploaded, setJustUploaded] = useState(false);

  // The gate shows only if the user is loaded AND has no photo yet.
  // `photo_path` is the server-side filename; `profile_photo_url` is a derived URL.
  // Admins and directivos are staff accounts, not Partner Match participants,
  // so we skip the gate for them.
  const hasPhoto = Boolean(user?.photo_path || user?.profile_photo_url);
  const isStaff = user?.role === "admin" || user?.role === "directivo";
  const shouldBlock = Boolean(user) && !hasPhoto && !justUploaded && !isStaff;

  // Prevent page scroll while gate is open.
  useEffect(() => {
    if (!shouldBlock) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [shouldBlock]);

  if (!shouldBlock) return null;

  const handlePhoto = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "photo.jpg");
      await api.post("/users/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJustUploaded(true);
      toast.success("¡Tu foto quedó lista! Ahora apareces en Partner Match.");
      await checkAuth();
    } catch (e) {
      toast.error("No se pudo subir la foto. Intenta de nuevo.");
    } finally { setUploading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 overflow-y-auto"
      data-testid="mandatory-photo-gate"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md bg-gradient-to-br from-[#1A0F2E] via-[#0F0F1F] to-[#0A0A0A] border-2 border-[#CCFF00]/50 rounded-2xl shadow-[0_30px_120px_rgba(204,255,0,0.25)] overflow-hidden">
        <div className="p-5 sm:p-7">
          <p className="text-xs tracking-[0.3em] uppercase text-[#CCFF00] font-black mb-1 flex items-center gap-1">
            <HandWaving size={14} weight="fill" /> ¡Bienvenido {user?.name?.split(" ")[0] || "Jugador"}!
          </p>
          <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black uppercase text-white leading-tight mb-3">
            Súbete una foto para empezar
          </h2>
          <p className="text-sm text-white/70 mb-4">
            Tu foto aparecerá en <span className="text-[#CCFF00] font-bold">Partner Match</span>, retas, clínicas y la comunidad.
            Es rápido: toma una selfie o súbe una desde tu galería.
          </p>

          {uploading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <div className="w-12 h-12 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-white/60">Subiendo foto...</p>
            </div>
          ) : (
            <div className="space-y-3">
              <PhotoCapture onPhotoTaken={handlePhoto} />
              <div className="flex items-center gap-2 pt-2 border-t border-white/5 text-[11px] text-white/50">
                <SealCheck size={14} className="text-[#CCFF00] shrink-0" weight="fill" />
                <span>Tu foto es privada; solo la ven jugadores autenticados de la plataforma.</span>
              </div>
            </div>
          )}
        </div>

        {/* No "later" button on purpose — admin chose foto obligatoria desde el primer login. */}
        <div className="bg-black/40 border-t border-white/5 px-5 py-3 flex items-center gap-2 text-[11px] text-white/50">
          <CameraRotate size={14} />
          <span>Podrás cambiar tu foto en cualquier momento desde Mi Perfil.</span>
        </div>
      </div>
    </div>
  );
}
